import React, { useRef, useState } from 'react';
import CameraCapture from './cameraCapture';
import ImageCropper from './imageCropper';

interface ProfilePictureInputProps {
  value: string | null;
  onChange: (imageDataUrl: string) => void;
}

const ProfilePictureInput: React.FC<ProfilePictureInputProps> = ({ value, onChange }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        setUploadedImage(dataUrl);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error reading file:', error);
      alert('Failed to load image');
    }

    // Reset input so the same file can be selected again
    e.target.value = '';
  };

  const handleCameraClick = () => {
    setShowCamera(true);
  };

  const handleCameraCapture = async (imageDataUrl: string) => {
    setShowCamera(false);
    // Show cropper for camera captures too, for consistency
    setUploadedImage(imageDataUrl);
    setShowCropper(true);
  };

  const handleCameraCancel = () => {
    setShowCamera(false);
  };

  const handleCropConfirm = (croppedImageDataUrl: string) => {
    setShowCropper(false);
    setUploadedImage(null);
    onChange(croppedImageDataUrl);
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setUploadedImage(null);
  };

  return (
    <div className="flex flex-col items-center gap-4 mb-4">
      <div className="w-[150px] h-[150px] rounded-full overflow-hidden bg-[var(--bg-secondary)] flex items-center justify-center border-3 border-[var(--border-color)]">
        {value ? (
          <img src={value} alt="Profile" className="w-full h-full object-cover" />
        ) : (
          <div className="text-[var(--text-secondary)] flex items-center justify-center">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
        )}
      </div>

      <div className="flex gap-3 flex-wrap justify-center">
        <button
          type="button"
          className="px-5 py-2.5 text-sm border-2 border-indigo-600 rounded-lg bg-[var(--bg-primary)] text-indigo-600 cursor-pointer transition-all font-medium hover:bg-indigo-600 hover:text-white active:scale-[0.98]"
          onClick={handleUploadClick}
        >
          📷 Upload Photo
        </button>
        <button
          type="button"
          className="px-5 py-2.5 text-sm border-2 border-indigo-600 rounded-lg bg-[var(--bg-primary)] text-indigo-600 cursor-pointer transition-all font-medium hover:bg-indigo-600 hover:text-white active:scale-[0.98]"
          onClick={handleCameraClick}
        >
          🤳 Take Selfie
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {showCamera && (
        <CameraCapture
          onCapture={handleCameraCapture}
          onCancel={handleCameraCancel}
        />
      )}

      {showCropper && uploadedImage && (
        <ImageCropper
          imageDataUrl={uploadedImage}
          onCrop={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
};

export default ProfilePictureInput;
